import React, { useRef } from "react";
import { useIsMutating } from "@tanstack/react-query";
import { Toast } from "primereact/toast";
import { ActionColumnButtonWrapper } from "../styledComponents";
import {
  getPayrollActionKeyPrefix,
  useApproveTimekeepingMutation,
} from "../../../../../api/bluecrew/hooks/payroll";
import { ActionButton, ActionButtonIcon } from "./styledComponents";
import { ActionButtonIconPathPrefix } from "./index";
import { buildShiftKey } from "../../../shiftUtils";
import { Tooltip } from "../../../../../components/Tooltip";
import { TimesheetsShiftInfo } from "../../../types/Shift.types";
import { logError } from "../../../../../sentry";
import { filterCompleteBreakTimestamps } from "../../breakUtils";

export type ButtonApproveProps = {
  rowData: TimesheetsShiftInfo;
  disabled: boolean;
};

export const ButtonApprove = ({ disabled, rowData }: ButtonApproveProps) => {
  const { user, job, company, userShift } = rowData;

  const toast = useRef<Toast>(null);
  const showErrorToast = (message: string) => {
    toast.current?.show({ severity: "error", summary: "Error", detail: message });
  };

  const approvePayload = {
    companyId: company.externalId,
    userId: user.externalId,
    jobId: job.externalId,
    shiftIndex: job.shiftIndex,
    shiftStart: userShift.start.time,
    shiftEnd: userShift.end.time,
    breaks: filterCompleteBreakTimestamps(userShift.break.breakTimestamps).map((item) => ({
      start: item.start.time,
      end: item.end.time,
    })),
  };

  const shiftKey = buildShiftKey(rowData);
  const tooltipAnchor = `approve-${shiftKey.cmId}-${shiftKey.jobId}-${shiftKey.shiftIndex}`;
  const { mutate } = useApproveTimekeepingMutation(shiftKey, showErrorToast);
  const isAnyPayrollActionMutating = !!useIsMutating(getPayrollActionKeyPrefix(shiftKey));

  const handleApprove = () => {
    const epochDate = new Date(0).toISOString();
    if (
      Number.isNaN(new Date(approvePayload.shiftStart).getTime()) ||
      Number.isNaN(new Date(approvePayload.shiftEnd).getTime()) ||
      approvePayload.shiftStart.toISOString() === epochDate ||
      approvePayload.shiftEnd.toISOString() === epochDate
    ) {
      logError({
        error: new Error(`Approve Shift: Invalid shift start or end time`),
        context: JSON.stringify(approvePayload),
      });
    }
    mutate(approvePayload);
  };

  return (
    <ActionColumnButtonWrapper>
      <Toast ref={toast} />
      <Tooltip position={"top"} target={`.${tooltipAnchor}`}>
        {"Approve"}
      </Tooltip>
      <ActionButton
        className={tooltipAnchor}
        disabled={disabled || isAnyPayrollActionMutating}
        icon={<ActionButtonIcon src={`${ActionButtonIconPathPrefix}/approve.svg`} />}
        rounded
        outlined
        onClick={handleApprove}
      />
    </ActionColumnButtonWrapper>
  );
};
